import { useState, useEffect, useRef } from "react";
import { useTheme } from "../../../theme";
import { BRAND_COLORS } from "../../../theme/colors.js";

// POSTs the form payload to Klaviyo's public client-subscriptions endpoint.
// Phone is stored as a custom `phone_raw` property (no SMS consent flag set).
// Both IDs are public; surface from VITE_* env vars so they can be rotated.
async function subscribeToKlaviyo({ fullName, email, phone, message }) {
  const companyId = import.meta.env.VITE_KLAVIYO_COMPANY_ID;
  const listId = import.meta.env.VITE_KLAVIYO_LIST_ID;
  if (!companyId || !listId) {
    throw new Error("missing_klaviyo_config");
  }

  const trimmed = fullName.trim();
  const firstSpace = trimmed.indexOf(" ");
  const firstName = firstSpace === -1 ? trimmed : trimmed.slice(0, firstSpace);
  const lastName = firstSpace === -1 ? "" : trimmed.slice(firstSpace + 1).trim();

  const properties = { message: message.trim(), source: "TLA Studios contact form" };
  if (phone && phone.trim()) properties.phone_raw = phone.trim();

  const profileAttributes = {
    email: email.trim(),
    properties,
  };
  if (firstName) profileAttributes.first_name = firstName;
  if (lastName) profileAttributes.last_name = lastName;

  const body = {
    data: {
      type: "subscription",
      attributes: {
        custom_source: "TLA Studios contact form",
        profile: {
          data: { type: "profile", attributes: profileAttributes },
        },
      },
      relationships: {
        list: { data: { type: "list", id: listId } },
      },
    },
  };

  const res = await fetch(
    `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(companyId)}`,
    {
      method: "POST",
      headers: {
        accept: "application/json",
        "content-type": "application/json",
        revision: "2024-10-15",
      },
      body: JSON.stringify(body),
    }
  );

  if (res.status !== 202) {
    let detail = `klaviyo_${res.status}`;
    try {
      const errBody = await res.json();
      const first = errBody?.errors?.[0]?.detail;
      if (first) detail = first;
    } catch {
      /* ignore */
    }
    const err = new Error(detail);
    err.status = res.status;
    throw err;
  }
}

export default function ContactModal({ open, onClose }) {
  const t = useTheme();
  const [form, setForm] = useState({ fullName: "", email: "", phone: "", message: "" });
  const [errors, setErrors] = useState({});
  // status drives the modal body: form (idle/submitting) → success or error step → close.
  const [status, setStatus] = useState("idle"); // 'idle' | 'submitting' | 'success' | 'error'
  const firstFieldRef = useRef(null);
  const closeTimerRef = useRef(null);

  const submitting = status === "submitting";

  // Reset form on close; lock body scroll + autofocus on open.
  useEffect(() => {
    if (!open) {
      setForm({ fullName: "", email: "", phone: "", message: "" });
      setErrors({});
      setStatus("idle");
      if (closeTimerRef.current) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
      return;
    }
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const focusId = setTimeout(() => firstFieldRef.current?.focus(), 50);
    return () => {
      clearTimeout(focusId);
      document.body.style.overflow = prevOverflow;
    };
  }, [open]);

  // Escape-key dismiss.
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onClose]);

  if (!open) return null;

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.fullName.trim()) next.fullName = "Please tell us your name.";
    if (!form.email.trim()) {
      next.email = "Please share an email address.";
    } else if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) {
      next.email = "That doesn't look like a valid email.";
    }
    if (!form.message.trim()) next.message = "Please write a short message.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus("submitting");
    try {
      await subscribeToKlaviyo(form);
      setStatus("success");
      closeTimerRef.current = setTimeout(onClose, 2200);
    } catch (err) {
      console.error("[contact] Klaviyo submission failed:", err);
      setStatus("error");
      closeTimerRef.current = setTimeout(onClose, 4000);
    }
  };

  const tryAgain = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setStatus("idle");
  };

  const inputBaseStyle = {
    width: "100%",
    background: t.surface,
    border: `1px solid ${t.border}`,
    color: t.text,
    padding: "12px 14px",
    borderRadius: 10,
    fontSize: 14,
    fontFamily: "inherit",
    outline: "none",
    transition: "border-color 0.15s ease",
    boxSizing: "border-box",
  };
  const inputStyleFor = (key) => ({
    ...inputBaseStyle,
    borderColor: errors[key] ? BRAND_COLORS.red : t.border,
  });
  const labelStyle = {
    display: "block",
    fontSize: 13,
    fontWeight: 500,
    color: t.textHigh,
    marginBottom: 6,
  };
  const errorStyle = { fontSize: 13, color: BRAND_COLORS.red, marginTop: 4 };
  const handleFocus = (e) => { e.currentTarget.style.borderColor = BRAND_COLORS.cyan; };
  const handleBlur = (key) => (e) => {
    e.currentTarget.style.borderColor = errors[key] ? BRAND_COLORS.red : t.border;
  };

  return (
    <div
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="contact-modal-title"
      style={{
        position: "fixed", inset: 0, zIndex: 1000,
        background: t.overlayBg,
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 24,
        animation: "fadeIn 0.3s ease",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 520,
          background: t.bg,
          border: `1px solid ${t.borderStrong}`,
          borderRadius: 16,
          padding: "32px clamp(20px, 4vw, 36px)",
          position: "relative",
          maxHeight: "calc(100vh - 48px)",
          overflowY: "auto",
          boxShadow: `0 24px 60px ${t.shadowStrong}`,
        }}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            position: "absolute", top: 16, right: 16,
            width: 36, height: 36, borderRadius: "50%",
            background: "transparent",
            border: `1px solid ${t.border}`,
            color: t.textHigh,
            fontSize: 20, lineHeight: 1, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            transition: "all 0.2s ease",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = t.surfaceHover; e.currentTarget.style.borderColor = t.borderStrong; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.borderColor = t.border; }}
        >×</button>

        {status === "success" || status === "error" ? (
          <div style={{ textAlign: "center", padding: "32px 0 8px" }}>
            <div
              aria-hidden="true"
              style={{
                width: 56, height: 56, margin: "0 auto 20px",
                borderRadius: "50%",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 28, lineHeight: 1, fontWeight: 700,
                background: status === "success" ? `${BRAND_COLORS.green}22` : `${BRAND_COLORS.red}22`,
                color: status === "success" ? BRAND_COLORS.green : BRAND_COLORS.red,
                border: `1.5px solid ${status === "success" ? BRAND_COLORS.green : BRAND_COLORS.red}`,
              }}
            >
              {status === "success" ? "✓" : "!"}
            </div>
            <h2 id="contact-modal-title" style={{
              fontSize: 22, fontWeight: 700, letterSpacing: -0.4,
              color: t.text, margin: "0 0 8px",
            }}>
              {status === "success" ? "Thanks for reaching out!" : "There's been an issue!"}
            </h2>
            <p style={{
              fontSize: 14, color: t.textMid, lineHeight: 1.5,
              margin: "0 auto", maxWidth: 380,
            }}>
              {status === "success"
                ? "We've received your message. Someone from the team will be in touch soon."
                : "We couldn't deliver your message. Please try again in a moment."}
            </p>
            {status === "error" && (
              <button
                type="button"
                onClick={tryAgain}
                style={{
                  marginTop: 20,
                  padding: "10px 22px",
                  background: "transparent",
                  color: t.textHigh,
                  border: `1px solid ${t.borderStrong}`,
                  borderRadius: 100,
                  fontSize: 14, fontWeight: 600,
                  cursor: "pointer",
                  transition: "border-color 0.2s ease, background 0.2s ease",
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = t.surfaceHover; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              >Try again</button>
            )}
          </div>
        ) : (
          <>
        <h2 id="contact-modal-title" style={{
          fontSize: 24, fontWeight: 700, letterSpacing: -0.5,
          color: t.text, margin: "0 0 8px",
        }}>Get in touch</h2>
        <p style={{
          fontSize: 14, color: t.textMid, lineHeight: 1.5,
          margin: "0 0 24px",
        }}>Tell us a bit about what you're working on.</p>

        <form onSubmit={onSubmit} noValidate style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div>
            <label style={labelStyle} htmlFor="contact-fullname">Full name *</label>
            <input
              ref={firstFieldRef}
              id="contact-fullname"
              type="text"
              value={form.fullName}
              onChange={(e) => setField("fullName", e.target.value)}
              onFocus={handleFocus}
              onBlur={handleBlur("fullName")}
              aria-invalid={Boolean(errors.fullName)}
              style={inputStyleFor("fullName")}
            />
            {errors.fullName && <div style={errorStyle}>{errors.fullName}</div>}
          </div>

          <div>
            <label style={labelStyle} htmlFor="contact-email">Email *</label>
            <input
              id="contact-email"
              type="email"
              value={form.email}
              onChange={(e) => setField("email", e.target.value)}
              onFocus={handleFocus}
              onBlur={handleBlur("email")}
              aria-invalid={Boolean(errors.email)}
              style={inputStyleFor("email")}
            />
            {errors.email && <div style={errorStyle}>{errors.email}</div>}
          </div>

          <div>
            <label style={labelStyle} htmlFor="contact-phone">Phone number</label>
            <input
              id="contact-phone"
              type="tel"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="tel"
              value={form.phone}
              onChange={(e) => setField("phone", e.target.value.replace(/\D/g, ""))}
              onFocus={handleFocus}
              onBlur={handleBlur("phone")}
              style={inputStyleFor("phone")}
            />
          </div>

          <div>
            <label style={labelStyle} htmlFor="contact-message">Message *</label>
            <textarea
              id="contact-message"
              rows={5}
              value={form.message}
              onChange={(e) => setField("message", e.target.value)}
              onFocus={handleFocus}
              onBlur={handleBlur("message")}
              aria-invalid={Boolean(errors.message)}
              style={{ ...inputStyleFor("message"), resize: "vertical", minHeight: 110 }}
            />
            {errors.message && <div style={errorStyle}>{errors.message}</div>}
          </div>

          <button
            type="submit"
            disabled={submitting}
            style={{
              marginTop: 8,
              padding: "14px 28px",
              background: t.text,
              color: t.bg,
              border: "none",
              borderRadius: 100,
              fontSize: 15,
              fontWeight: 700,
              letterSpacing: -0.2,
              cursor: submitting ? "not-allowed" : "pointer",
              opacity: submitting ? 0.6 : 1,
              transition: "transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease",
            }}
            onMouseEnter={(e) => {
              if (submitting) return;
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = `0 12px 32px ${t.shadowStrong}`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >{submitting ? "Sending…" : "Send"}</button>
        </form>
          </>
        )}
      </div>
    </div>
  );
}
